import React, { useCallback } from 'react';
import { useCreateSetupIntentForBookingMutation } from '../../services/stripeService/__generated__/queries.generated';
import { CardSetupForm } from '../CardSetupForm';

interface NewCardFormProps {
  bookingId: string;
  /** Pays with the collected card. Resolves when the payment attempt is over. */
  onCardReady: (paymentMethodId: string) => Promise<void>;
  busy: boolean;
  disabled: boolean;
}

/**
 * Spec 013 P3: collects a new card for an extension through a SetupIntent on the booking's
 * customer (with the guest's saved cards and the "save" box), then hands its id to the
 * extension payment. Remount it (change its key) after a refusal: the SetupIntent is spent.
 */
export const NewCardForm: React.FC<NewCardFormProps> = ({
  bookingId,
  onCardReady,
  busy,
  disabled,
}) => {
  const [createSetupIntent] = useCreateSetupIntentForBookingMutation();
  const loadSetup = useCallback(async () => {
    const { data } = await createSetupIntent({
      // The list above already offers the trip's and saved cards: open on an empty card form.
      variables: { bookingId, showSavedCards: false },
    });
    const answer = data?.createSetupIntentForBooking;
    if (!answer?.clientSecret)
      throw new Error('The card form could not be opened. Please try again.');
    return {
      clientSecret: answer.clientSecret,
      customerSessionClientSecret: answer.customerSessionClientSecret,
    };
  }, [createSetupIntent, bookingId]);

  return (
    <CardSetupForm
      loadSetup={loadSetup}
      onCardReady={({ paymentMethodId }) => onCardReady(paymentMethodId)}
      submitLabel="Pay with this card"
      notice={
        <>
          Use a card in your own name. Your bank may ask you to confirm this payment. Tick
          &quot;Save&quot; above to use the card on future trips.
        </>
      }
      busy={busy}
      disabled={disabled}
    />
  );
};
